import {
	createAsyncThunk,
	createSelector,
	createSlice,
	PayloadAction,
} from "@reduxjs/toolkit";
import { AppThunk, GenericSelector, RootState } from "../../app/store";
import { BoundingBox } from "../../common/BoundingBox";
import { generateUniqueId } from "../../common/generateUniqueId";
import { Point2D } from "../../common/Point2D";
import { fetchLogicsConfiguration, selectLogics } from "../logics/logicsSlice";
import { BlockSource } from "../windowManager/window/windowImpl/BlockConfigWindow/BlockConfigWindow";
import Connection, {
	ConnectionIo,
	ConnectionIoMemoryRef,
	ConnectionIoOperator,
} from "./interfaces/components/Connection";

import EditorState, {
	BlockPreview,
	FileInfoOneDrive,
	SideBarSelection,
} from "./EditorState";
import Device from "./interfaces/components/Device";
import Memory, { MemorySourceIo } from "./interfaces/components/Memory";
import MemoryRef from "./interfaces/components/MemoryRef";
import Operator from "./interfaces/components/Operator";
import {
	VariableContent,
	variableFromLogics,
} from "./interfaces/components/Variable";

import { IPublicClientApplication } from "@azure/msal-browser";
import { t, TFunction } from "i18next";
import { difference, isArray, uniqBy } from "lodash";
import { toast } from "sonner";
import SaveFormat from "../../common/save/SaveFormat";
import {
	fetchSaveProject,
	fetchUpdateProjectName,
} from "../../services/microsoft";
import { IoErrors, Toast } from "./components/toast/Toast";
import {
	createSheetState,
	isInSelection,
	isSameSelection,
	Selection,
	SelectionIo,
	SelectionMemoryRef,
	SelectionMultiple,
	SelectionOperator,
	Sheet,
} from "./interfaces/Sheet";

export function findDeviceAddress(
	deviceId: string,
	devices: (Device | null)[],
): string | undefined {
	const device = devices.find((device) => device?.id === deviceId);

	if (device === undefined || device === null) {
		return undefined;
	}

	return device.address ?? undefined;
}

export * from "./EditorState";

export type BlockPreviewTrigger = "click" | "drag" | null;

interface EditorStateExtraProps {
	undo: Array<Sheet[]>;
	redo: Array<Sheet[]>;
	dummy: undefined;
	blockPreviewTrigger: BlockPreviewTrigger;
}

type FullEditorState = EditorState & EditorStateExtraProps;

const initialExtraState: EditorStateExtraProps = {
	undo: [],
	redo: [],
	dummy: undefined,
	blockPreviewTrigger: null,
};

type SaveProjectPayload = {
	instance: IPublicClientApplication;
	saveFormat: SaveFormat;
	t: TFunction;
	notify: boolean;
	driveId: string;
	itemId: string;
	update: boolean;
	newName: string | null;
};

interface UpdateProjectFileNamePayload {
	name: string;
	instance: IPublicClientApplication;
	driveId: string;
	fileId: string;
	t: TFunction;
}

export const saveProjectOneDrive = createAsyncThunk(
	"oneDrive/saveProject",
	async ({
		saveFormat,
		instance,
		t,
		notify,
		driveId,
		itemId,
		update,
		newName,
	}: SaveProjectPayload) => {
		try {
			const fileInfo: FileInfoOneDrive = await fetchSaveProject(
				instance,
				saveFormat,
				driveId,
				itemId,
				update,
				newName,
			);

			if (notify) toast.success(t("saveFile", { fileName: fileInfo.name }));

			return fileInfo;
		} catch (err) {
			toast.error(t("saveError"));
		}
	},
);

export const updateProjectFileNameOneDrive = createAsyncThunk(
	"oneDrive/updateProjectName",
	async ({
		name,
		instance,
		driveId,
		fileId,
		t,
	}: UpdateProjectFileNamePayload) => {
		try {
			await fetchUpdateProjectName(name, instance, driveId, fileId);

			toast.success("Nome atualizado com sucesso");
		} catch (err) {
			toast.error("Erro ao atualizar nome do arquivo");
		}
	},
);

const initialState: FullEditorState = {
	projectName: "New Project",
	currentTabIndex: 0,
	sheets: [createSheetState()],
	memories: {},
	devices: [],
	sideBarSelection: null,
	blockPreview: null,
	ram: {},
	...initialExtraState,
};

export const editorSlice = createSlice({
	name: "editor",
	initialState,
	reducers: {
		resetEditor: () => {
			return initialState;
		},
		loadEditorState: (_state, action: PayloadAction<EditorState>) => {
			return {
				...action.payload,
				...initialExtraState,
			};
		},
		createDeviceSlot: (state) => {
			state.devices.push(null);
		},
		updateDeviceSlot: (
			state,
			action: PayloadAction<{
				index: number;
				device: Device;
			}>,
		) => {
			state.devices[action.payload.index] = action.payload.device;
		},
		deleteDeviceSlot(
			state,
			action: PayloadAction<{
				index: number;
			}>,
		) {
			state.devices.splice(action.payload.index, 1);
		},
		clearDeviceSlot(state) {
			state.devices = state.devices.filter((device) => device !== null);
		},
		setSideBarSelection: (state, action: PayloadAction<SideBarSelection>) => {
			state.sideBarSelection = action.payload;
		},
		updateProjectName: (state, action: PayloadAction<string>) => {
			if (action.payload) {
				state.projectName = action.payload;
			}
		},
		selectTab: (state, action: PayloadAction<number>) => {
			if (action.payload < state.sheets.length) {
				state.currentTabIndex = action.payload;
			}
		},
		moveTab: (
			state,
			action: PayloadAction<{
				fromId: string;
				toId: string;
			}>,
		) => {
			const currentId = state.sheets[state.currentTabIndex].id;

			const fromIndex = state.sheets.findIndex(
				(sheet) => sheet.id === action.payload.fromId,
			);
			const toIndex = state.sheets.findIndex(
				(sheet) => sheet.id === action.payload.toId,
			);
			const removed = state.sheets.splice(fromIndex, 1);

			state.sheets.splice(toIndex, 0, ...removed);

			state.currentTabIndex = state.sheets.findIndex(
				(sheet) => sheet.id === currentId,
			);
		},
		createSheet: (state) => {
			state.sheets.push(createSheetState());
		},
		deleteSheet: (state, action: PayloadAction<{ sheetIndex: number }>) => {
			state.sheets.splice(action.payload.sheetIndex, 1);

			if (state.sheets.length === 0) {
				state.sheets.push(createSheetState());
				state.currentTabIndex = 0;
			}

			if (action.payload.sheetIndex < state.currentTabIndex) {
				state.currentTabIndex -= 1;
			}

			if (state.currentTabIndex >= state.sheets.length) {
				state.currentTabIndex = state.sheets.length - 1;
			}
		},
		moveSheetByOffset: (
			state,
			action: PayloadAction<{
				index: number;
				delta: Point2D;
			}>,
		) => {
			const sheet = state.sheets[action.payload.index];
			sheet.position.x += action.payload.delta.x;
			sheet.position.y += action.payload.delta.y;
		},
		moveSheetTo: (
			state,
			action: PayloadAction<{
				index: number;
				position: Point2D;
			}>,
		) => {
			const sheet = state.sheets[action.payload.index];
			sheet.position.x = action.payload.position.x;
			sheet.position.y = action.payload.position.y;
		},
		setSheetGridSize: (
			state,
			action: PayloadAction<{
				index: number;
				size: Point2D;
			}>,
		) => {
			const sheet = state.sheets[action.payload.index];
			sheet.gridSize.x = action.payload.size.x;
			sheet.gridSize.y = action.payload.size.y;
		},
		updateSheetTitle: (
			state,
			action: PayloadAction<{
				index: number;
				title: string;
			}>,
		) => {
			if (action.payload) {
				state.sheets[action.payload.index].title = action.payload.title;
			}
		},
		updateSheetScale: (
			state,
			action: PayloadAction<{
				index: number;
				scale: number;
			}>,
		) => {
			if (action.payload) {
				state.sheets[action.payload.index].scale = action.payload.scale;
			}
		},
		setBlockPreview: (
			state,
			action: PayloadAction<{
				preview: BlockPreview;
				trigger: BlockPreviewTrigger;
			}>,
		) => {
			state.blockPreview = action.payload.preview;
			state.blockPreviewTrigger = action.payload.trigger;
		},
		updateBlockPreviewBoundingBox: (
			state,
			action: PayloadAction<BoundingBox>,
		) => {
			if (state.blockPreview) {
				state.blockPreview.block.boundingBox = action.payload;
			}
		},
		insertOperator(
			state,
			action: PayloadAction<{
				operator: Operator | Operator[];
			}>,
		) {
			const { operator } = action.payload;

			if (Array.isArray(operator)) {
				const sheet = state.sheets[state.currentTabIndex];
				const oldIndex = state.sheets[state.currentTabIndex].operators.length;

				let selectedOperators: Record<string, boolean> | null = null;

				if (sheet.selection) {
					if (sheet.selection.type === "multiple") {
						selectedOperators = (sheet.selection as SelectionMultiple).selection
							.filter(
								(s) => s && s.type === "operator" && s.operatorId !== undefined,
							)
							.reduce((init, s) => {
								return {
									...init,
									[(s as SelectionOperator)?.operatorId]: true,
								};
							}, {});
					}
				}

				const filteredOperators =
					selectedOperators !== null
						? sheet.operators.filter((op) => selectedOperators[op.id])
						: sheet.operators;

				const newOperators = operator.map((operator) => {
					let newPosition = 50;

					while (
						filteredOperators.some(
							(op) =>
								op.boundingBox.top === operator.boundingBox.top + newPosition &&
								op.boundingBox.left === operator.boundingBox.left &&
								op.id !== operator.id,
						)
					) {
						newPosition += 50;
					}
					return {
						...operator,
						boundingBox: {
							...operator.boundingBox,
							top: operator.boundingBox.top + newPosition,
						},
					};
				});

				sheet.operators.push(...newOperators);

				const operators = newOperators.map((op, index) => {
					return {
						index: oldIndex + index,
						operatorId: op.id,
						type: "operator",
					};
				}) as Selection[];

				sheet.selection = {
					index: 0,
					type: "multiple",
					selection: [
						...(sheet?.selection
							? (sheet?.selection as SelectionMultiple).selection
							: []),
						...operators,
					],
				};
			} else {
				const sheet = state.sheets.find(
					(sheet) => sheet.id === operator.sheetId,
				);

				if (sheet) sheet.operators.push(operator);
			}
		},
		insertMemoryRef(
			state,
			action: PayloadAction<{
				memoryRef: MemoryRef | MemoryRef[];
			}>,
		) {
			const { memoryRef } = action.payload;

			if (Array.isArray(memoryRef)) {
				const sheet = state.sheets[state.currentTabIndex];
				const oldIndex = state.sheets[state.currentTabIndex].memoryRefs.length;

				let selectedMemoryRefs: Record<string, boolean> | null = null;

				if (sheet.selection) {
					if (sheet.selection.type === "multiple") {
						selectedMemoryRefs = (
							sheet.selection as SelectionMultiple
						).selection
							.filter(
								(s) =>
									s && s.type === "memoryRef" && s.memoryRefId !== undefined,
							)
							.reduce((init, s) => {
								return {
									...init,
									[(s as SelectionMemoryRef)?.memoryRefId]: true,
								};
							}, {});
					}
				}

				const filteredMemoryRefs =
					selectedMemoryRefs != null
						? sheet.memoryRefs.filter((op) => selectedMemoryRefs[op.id])
						: sheet.memoryRefs;

				const newMemoryRefs = memoryRef.map((memo) => {
					let newPosition = 50;

					while (
						filteredMemoryRefs.some(
							(m) =>
								m.boundingBox.top === memo.boundingBox.top + newPosition &&
								m.boundingBox.left === memo.boundingBox.left &&
								m.sourceMemoryId === memo.sourceMemoryId &&
								m.id !== memo.id,
						)
					) {
						newPosition += 50;
					}

					return {
						...memo,
						boundingBox: {
							...memo.boundingBox,
							top: memo.boundingBox.top + newPosition,
						},
					};
				});

				sheet.memoryRefs.push(...newMemoryRefs);

				const memoryRefs = newMemoryRefs.map((memo, index) => {
					return {
						index: oldIndex + index,
						memoryRefId: memo.id,
						type: "memoryRef",
					};
				}) as Selection[];

				sheet.selection = {
					index: 0,
					type: "multiple",
					selection: [
						...(sheet?.selection
							? (sheet?.selection as SelectionMultiple).selection
							: []),
						...memoryRefs,
					],
				};
			} else {
				const sheet = state.sheets.find(
					(sheet) => sheet.id === memoryRef.sheetId,
				);

				if (sheet) {
					sheet.memoryRefs.push(memoryRef);
				}
			}
		},
		insertMemory(
			state,
			action: PayloadAction<{
				memory: Memory;
			}>,
		) {
			if (state.memories[action.payload.memory.name] === undefined) {
				state.memories[action.payload.memory.name] = [];
			}

			state.memories[action.payload.memory.name].push(action.payload.memory);
		},
		updateMemory(
			state,
			action: PayloadAction<{
				memory: Memory;
				index: number;
			}>,
		) {
			const category = state.memories[action.payload.memory.name];
			category[action.payload.index] = action.payload.memory;
		},
		updateMemoryRef(
			state,
			action: PayloadAction<{
				sheetIndex: number;
				memoryRefId: string;
				data: { error: boolean };
			}>,
		) {
			const { sheetIndex, memoryRefId, data } = action.payload;
			const ref = state.sheets[sheetIndex].memoryRefs.find(
				(memoryRef) => memoryRef.id === memoryRefId,
			);

			if (ref) ref.error = data.error;
		},
		updateOperator(
			state,
			action: PayloadAction<{
				sheetIndex: number;
				operatorId: string;
				data: { error: boolean };
			}>,
		) {
			const { sheetIndex, operatorId, data } = action.payload;
			const ref = state.sheets[sheetIndex].operators.find(
				(operator) => operator.id === operatorId,
			);

			if (ref) ref.error = data.error;
		},
		updateConnection(
			state,
			action: PayloadAction<{
				sheetIndex: number;
				connectionId: string;
				data: { error: boolean };
			}>,
		) {
			const { sheetIndex, connectionId, data } = action.payload;
			const ref = state.sheets[sheetIndex].connections.find(
				(connection) => connection.id === connectionId,
			);

			if (ref) ref.error = data.error;
		},
		deleteOperator(
			state,
			action: PayloadAction<{
				sheetIndex: number;
				operatorId: string;
			}>,
		) {
			const sheet = state.sheets[action.payload.sheetIndex];
			sheet.operators = sheet.operators.filter(
				(operator) => operator.id !== action.payload.operatorId,
			);
		},
		deleteMemoryRef(
			state,
			action: PayloadAction<{
				sheetIndex: number;
				memoryRefId: string;
			}>,
		) {
			const sheet = state.sheets[action.payload.sheetIndex];
			sheet.memoryRefs = sheet.memoryRefs.filter(
				(memoryRef) => memoryRef.id !== action.payload.memoryRefId,
			);
		},
		deleteMemory(
			state,
			action: PayloadAction<{
				memoryName: string;
				index: number;
			}>,
		) {
			const category = state.memories[action.payload.memoryName];
			category.splice(action.payload.index, 1);
		},
		deleteMemories(
			state,
			action: PayloadAction<{
				memoryName: string;
				indexes: number[];
			}>,
		) {
			const { memoryName, indexes } = action.payload;
			const newCategory = state.memories[memoryName].filter(
				(_, index) => !indexes.includes(index),
			);

			state.memories[memoryName] = newCategory;
		},
		updateBlockBoundingBox(
			state,
			action: PayloadAction<{
				sheetIndex: number;
				source: BlockSource;
				boundingBox: BoundingBox;
			}>,
		) {
			const { sheetIndex, source, boundingBox } = action.payload;

			switch (source.type) {
				case "operator":
					const operator = state.sheets[sheetIndex].operators.find(
						(operator) => operator.id === source.operatorId,
					);

					if (operator) {
						operator.boundingBox = boundingBox;
					}

					break;

				case "memoryRef":
					const memoryRef = state.sheets[sheetIndex].memoryRefs.find(
						(memoryRef) => memoryRef.id === source.memoryRefId,
					);

					if (memoryRef) {
						memoryRef.boundingBox = boundingBox;
					}

					break;
			}
		},
		updateMemoryRefBoundingBoxByIndex(
			state,
			action: PayloadAction<{
				blockIndex: number;
				sheetIndex: number;
				boundingBox: BoundingBox;
			}>,
		) {
			const { sheetIndex, boundingBox, blockIndex } = action.payload;

			state.sheets[sheetIndex].memoryRefs[blockIndex].boundingBox = boundingBox;
		},
		updateOperatorBoundingBoxByIndex(
			state,
			action: PayloadAction<{
				blockIndex: number;
				sheetIndex: number;
				boundingBox: BoundingBox;
			}>,
		) {
			const { sheetIndex, boundingBox, blockIndex } = action.payload;

			state.sheets[sheetIndex].operators[blockIndex].boundingBox = boundingBox;
		},
		setSelection(state, action: PayloadAction<Selection>) {
			const currentTabIndex = state.currentTabIndex;
			state.sheets[currentTabIndex].selection = action.payload;
		},
		addToSelection(state, action: PayloadAction<Selection>) {
			const currentTabIndex = state.currentTabIndex;
			const selection = state.sheets[currentTabIndex].selection;

			if (selection === null) {
				state.sheets[currentTabIndex].selection = action.payload;
				return;
			}

			if (selection.type === "multiple") {
				selection.selection.push(action.payload);
				return;
			}

			state.sheets[currentTabIndex].selection = {
				index: 0,
				type: "multiple",
				selection: [selection, action.payload],
			};
		},
		selectMultiple(state, action: PayloadAction<Selection[]>) {
			const currentTabIndex = state.currentTabIndex;

			state.sheets[currentTabIndex].selection = {
				index: 0,
				type: "multiple",
				selection: action.payload,
			};
			return;
		},
		removeFromSelection(state, action: PayloadAction<Selection>) {
			const currentTabIndex = state.currentTabIndex;
			const selection = state.sheets[currentTabIndex].selection;

			if (selection === null) {
				return;
			}

			if (selection.type === "multiple") {
				selection.selection = selection.selection.filter(
					(selection) => !isSameSelection(selection, action.payload),
				);
				return;
			}

			if (isSameSelection(selection, action.payload)) {
				state.sheets[currentTabIndex].selection = null;
			}
		},
		updateBlockVariableContent(
			state,
			action: PayloadAction<{
				source: BlockSource;
				variableName: string;
				content: VariableContent;
			}>,
		) {
			const { source, variableName, content } = action.payload;

			switch (source.type) {
				case "operator":
					const operator = state.sheets[source.sheetIndex].operators.find(
						(operator) => operator.id === source.operatorId,
					);

					if (operator) {
						operator.metadata.variables[variableName].content = content;
					}

					break;

				case "memoryRef":
					const memoryRef = state.sheets[source.sheetIndex].memoryRefs.find(
						(memoryRef) => memoryRef.id === source.memoryRefId,
					);

					if (memoryRef === undefined) {
						break;
					}

					const category = state.memories[memoryRef.name];

					const memory = category.find(
						(memory) => memory.id === memoryRef.sourceMemoryId,
					);

					if (memory) {
						memory.metadata.variables[variableName].content = content;
					}

					break;
			}
		},
		updateBlockDataValue(
			state,
			action: PayloadAction<{
				source: BlockSource;
				dataName: string;
				value: string | string[];
			}>,
		) {
			const { source, dataName: variableName, value: content } = action.payload;

			switch (source.type) {
				case "operator":
					const operator = state.sheets[source.sheetIndex].operators.find(
						(operator) => operator.id === source.operatorId,
					);

					if (operator) {
						operator.metadata.data[variableName] = content;
					}

					break;

				case "memoryRef":
					const memoryRef = state.sheets[source.sheetIndex].memoryRefs.find(
						(memoryRef) => memoryRef.id === source.memoryRefId,
					);

					if (memoryRef === undefined) {
						break;
					}

					const category = state.memories[memoryRef.name];
					const memory = category.find(
						(memory) => memory.id === memoryRef.sourceMemoryId,
					);

					if (memory) {
						memory.metadata.data[variableName] = content;
					}

					break;
			}
		},
		updateOperatorEnabledIos(
			state,
			action: PayloadAction<{
				sheetIndex: number;
				operatorId: string;
				ioType: "input" | "output";
				ioName: string;
				enabled: boolean;
			}>,
		) {
			const operator = state.sheets[action.payload.sheetIndex].operators.find(
				(operator) => operator.id === action.payload.operatorId,
			);

			if (operator) {
				switch (action.payload.ioType) {
					case "input":
						operator.inputs.find(
							(io) => io.name === action.payload.ioName,
						)!.enabled = action.payload.enabled;
						break;

					case "output":
						operator.outputs.find(
							(io) => io.name === action.payload.ioName,
						)!.enabled = action.payload.enabled;
						break;
				}
			}
		},
		updateMemoryRefSource(
			state,
			action: PayloadAction<{
				sheetId: string;
				memoryRefId: string;
				sourceMemoryId: string | null;
			}>,
		) {
			const { sheetId, memoryRefId, sourceMemoryId } = action.payload;

			const memoryRef = state.sheets
				.find((sheet) => sheet.id === sheetId)
				?.memoryRefs.find((memoryRef) => memoryRef.id === memoryRefId);

			if (memoryRef) {
				memoryRef.sourceMemoryId = sourceMemoryId;
			}
		},
		createConnection(
			state,
			action: PayloadAction<
				| {
						id: string;
						origin: ConnectionIo;
						destination: ConnectionIo;
				  }
				| {
						id: string;
						from: ConnectionIo;
						to: ConnectionIo;
						error: boolean;
				  }[]
			>,
		) {
			if (isArray(action.payload)) {
				const currentSheet = state.sheets[state.currentTabIndex];

				currentSheet.connections.push(...action.payload);

				const connections = action.payload.map((op, index) => {
					return {
						index,
						connectionId: op.id,
						type: "connection",
					};
				}) as Selection[];

				currentSheet.selection = {
					index: 0,
					type: "multiple",
					selection: [
						...(currentSheet?.selection
							? (currentSheet?.selection as SelectionMultiple).selection
							: []),
						...connections,
					],
				};
			} else {
				let { id, origin, destination } = action.payload;

				if (origin.ioType === "input" && destination.ioType === "input") {
					return;
				}

				if (origin.ioType === "output" && destination.ioType === "output") {
					return;
				}

				if (origin.type === "memoryRef" && destination.type === "memoryRef") {
					return;
				}

				if (origin.ioType === "input") {
					[origin, destination] = [destination, origin];
				}

				const currentSheet = state.sheets[state.currentTabIndex];

				// check if destination is already connected by checking the type and id
				switch (destination.type) {
					case "operator": {
						const destinationIo = destination as ConnectionIoOperator;
						const operator = currentSheet.operators.find(
							(operator) => operator.id === destinationIo.operatorId,
						);

						if (operator === undefined) {
							return;
						}

						if (
							currentSheet.connections.some(
								(connection) =>
									connection.to.type === "operator" &&
									connection.to.operatorId === destinationIo.operatorId &&
									connection.to.ioGroup === destinationIo.ioGroup &&
									connection.to.ioIndex === destinationIo.ioIndex,
							)
						) {
							return;
						}

						break;
					}
					case "memoryRef": {
						const destinationIo = destination as ConnectionIoMemoryRef;
						const memoryRef = currentSheet.memoryRefs.find(
							(memoryRef) => memoryRef.id === destinationIo.memoryRefId,
						);

						if (memoryRef === undefined) {
							return;
						}

						if (
							currentSheet.connections.some(
								(connection) =>
									connection.to.type === "memoryRef" &&
									connection.to.memoryRefId === destinationIo.memoryRefId,
							)
						) {
							return;
						}

						break;
					}
				}

				currentSheet.connections.push({
					id,
					from: origin,
					to: destination,
					error: false,
				});
			}
		},
		deleteConnection(
			state,
			action: PayloadAction<{
				id: string;
			}>,
		) {
			const currentSheet = state.sheets[state.currentTabIndex];
			const index = currentSheet.connections.findIndex(
				(connection) => connection.id === action.payload.id,
			);
			if (index !== -1) {
				currentSheet.connections.splice(index, 1);
			}
		},
		addUndo(state) {
			state.undo = [...state.undo, state.sheets];
		},
		undo(state) {
			const lastUndo = state.undo.at(-1);
			state.undo = state.undo.slice(0, -1);

			if (lastUndo) {
				state.redo = [...state.redo, state.sheets];
				state.sheets = lastUndo;
			}
		},
		redo(state) {
			const lastRedo = state.redo.at(-1);
			state.redo = state.redo.slice(0, -1);

			if (lastRedo) {
				state.undo = [...state.undo, state.sheets];
				state.sheets = lastRedo;
			}
		},
		updateRam(state, action: PayloadAction<Record<string, number>>) {
			state.ram = action.payload;
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(fetchLogicsConfiguration.fulfilled, (state, action) => {
				const languages = ["Portuguese", "Spanish", "English"];
				const majorVersion = Number(action.payload.commit.name.split(".")[1]);
				const ioErrors: Array<IoErrors> = [];
				const sourceMemories: Map<
					string,
					{ memoryRef: MemoryRef; sheet: number }
				> = new Map();

				if (action.payload.migrate) {
					const { operator, memory } = action.payload;
					const removedItems = new Map<
						string,
						{ type: "operator" | "memoryRef"; name: string }
					>();
					let removedIos: Array<string> = [];

					state.devices.forEach((device) => {
						const logicDevice = action.payload.device[device!.label];

						device!.configs = Object.entries(logicDevice.config).reduce(
							(init, [key, value]) => {
								const logicConfig = action.payload.config[key];

								const newValue = value.map((v) => {
									const newVariables = logicConfig.variables
										.filter((variable) => variable.name !== "config_type")
										.reduce((init, value) => {
											return {
												...init,
												[value.label]: {
													content: {
														type: value.content.type,
														value: value.content.default,
													},
												},
											};
										}, {});

									return {
										...v,
										variables: newVariables,
									};
								});

								return {
									...init,
									[key]: newValue,
								};
							},
							{},
						);

						// device.address = logicDevice.addresses
						const oldIos = [
							...device!.outputs.map((out) => out.name),
							...device!.inputs.map((ipt) => ipt.name),
						];
						const newIos = [
							...Object.keys(logicDevice.output),
							...Object.keys(logicDevice.input),
						];

						removedIos = [...removedIos, ...difference(oldIos, newIos)];

						device!.outputs = Object.entries(logicDevice.output).map(
							([key, value], idx) => {
								const output = device?.outputs.find((o) => o.name === key);

								if (output) {
									const { name } = output;
									const { id, ...newVariables } = value;

									return {
										id: idx,
										name,
										...Object.entries(newVariables).reduce(
											(acc, [k, variable]) => {
												return {
													...acc,
													...(output[k] === undefined
														? {
																[k]: isArray(variable) ? variable[0] : variable,
															}
														: { [k]: output[k] }),
												};
											},
											{},
										),
									};
								} else {
									const { id, ...rest } = value;

									return {
										id: idx,
										name: key,
										...Object.entries(rest).reduce(
											(init, [k, v]) => ({
												...init,
												[k]: Array.isArray(v) ? v[0] : v,
											}),
											{},
										),
									};
								}
							},
						);

						device!.inputs = Object.entries(logicDevice.input).map(
							([key, value], idx) => {
								const input = device?.inputs.find((o) => o.name === key);

								if (input) {
									const { name } = input;
									const { id, ...newVariables } = value;

									return {
										id: id,
										name,
										...Object.entries(newVariables).reduce(
											(acc, [k, variable]) => {
												return {
													...acc,
													...(input[k] === undefined
														? {
																[k]: isArray(variable) ? variable[0] : variable,
															}
														: { [k]: input[k] }),
												};
											},
											{},
										),
									};
								} else {
									const { id, ...rest } = value;

									return {
										id: idx,
										name: key,
										...Object.entries(rest).reduce(
											(init, [k, v]) => ({
												...init,
												[k]: Array.isArray(v) ? v[0] : v,
											}),
											{},
										),
									};
								}
							},
						);
					});

					state.sheets.forEach((sheet, idx) => {
						sheet.operators = sheet.operators
							.filter((op) => {
								if (operator[op.name] === undefined) {
									removedItems.set(op.id, { type: "operator", name: op.name });
								}
								return operator[op.name] !== undefined;
							})
							.map((op) => {
								const newOperator = operator[op.name];

								const newInputs = newOperator.inputs.map((input) => {
									const foundInput = op.inputs.find(
										(i) => i.name == input.label,
									);

									if (foundInput) return foundInput;

									return {
										name: input.label,
										enabled: input.enabledByDefault,
									};
								});

								const newOutputs = newOperator.outputs.map((output) => {
									const foundOutput = op.outputs.find(
										(i) => i.name == output.label,
									);

									if (foundOutput) return foundOutput;

									return {
										name: output.label,
										enabled: output.enabledByDefault,
									};
								});

								const newVariables = newOperator.variables.reduce((init, v) => {
									const alreadyExist = op.metadata.variables[v.name];

									if (alreadyExist) {
										return {
											...init,
											[v.name]: alreadyExist,
										};
									}

									return {
										...init,
										[v.name]: {
											label: v.label,
											content: {
												type: v.content.type,
												value: v.content.default,
											},
										},
									};
								}, {});

								return {
									...op,
									inputs: newInputs,
									outputs: newOutputs,
									metadata: {
										variables: newVariables,
										data: {},
									},
								};
							});

						sheet.memoryRefs = sheet.memoryRefs.filter((memo) => {
							sourceMemories.set(`${memo.sourceMemoryId}`, {
								memoryRef: memo,
								sheet: idx,
							});

							if (memory[memo.name] === undefined) {
								removedItems.set(memo.id, {
									type: "memoryRef",
									name: memo.name,
								});
							}
							return memory[memo.name] !== undefined;
						});

						sheet.connections = sheet.connections.filter((connection) => {
							return (
								!removedItems.has(
									(connection.from as ConnectionIoOperator).operatorId,
								) &&
								!removedItems.has(
									(connection.to as ConnectionIoOperator).operatorId,
								) &&
								!removedItems.has(
									(connection.from as ConnectionIoMemoryRef).memoryRefId,
								) &&
								!removedItems.has(
									(connection.to as ConnectionIoMemoryRef).memoryRefId,
								)
							);
						});
					});

					state.memories = Object.entries(state.memories)
						.filter(([key, _]) => memory[key] !== undefined)
						.reduce((init, [key, value]) => {
							const memo = memory[key];

							const newValue = value
								.filter((input) => {
									const removed = removedIos.includes(
										(input.source as MemorySourceIo)?.io,
									);
									if (removed) {
										const memo = sourceMemories.get(input.id);

										ioErrors.push({
											id: memo!.memoryRef!.id,
											type: "memoryRef",
											box: {
												x:
													memo!.memoryRef!.boundingBox.left +
													memo!.memoryRef!.boundingBox.width,
												y:
													memo!.memoryRef!.boundingBox.height +
													memo!.memoryRef!.boundingBox.top,
											},
											message: `Memory ${input.id} removed, because IO ${
												(input.source as MemorySourceIo).io
											} don't exist in this version`,
											sheet: memo!.sheet,
										});
									}
									return !removed;
								})
								.map((input) => {
									const newVariables = memo.variables.reduce((init, v) => {
										const alreadyExist = input.metadata.variables[v.name];

										if (alreadyExist) {
											return {
												...init,
												[v.name]: alreadyExist,
											};
										}

										return {
											...init,
											[v.name]: {
												label: v.label,
												content: {
													type: v.content.type,
													value: v.content.default,
												},
											},
										};
									}, {});

									const missingData = memo.data?.filter((v) => {
										return (
											input.metadata.data[v.size] === undefined &&
											input.metadata.data[v.label] === undefined
										);
									});

									if (majorVersion >= 15) {
										const hasMessage = Object.keys(input.metadata.data).find(
											(key, value) => key == "message_size",
										);

										if (hasMessage) {
											const message = input.metadata.data["message_size"];
											delete input.metadata.data.message_size;

											let arrayMessage = [];

											input.metadata.data = languages.reduce(
												(init, lang, index) => {
													const formattedLang = `message_size_${lang.toLocaleLowerCase()}`;

													if (!Array.isArray(message)) {
														arrayMessage.push(message);
													}

													return {
														...init,
														...(arrayMessage.length > 0
															? { message_size: arrayMessage }
															: {}),
														[formattedLang]: Array.isArray(message)
															? message[index]
															: message,
													};
												},
												{
													...input.metadata.data,
													message_size: Array.isArray(message)
														? message.toSpliced(languages.length)
														: [],
												},
											);
										}
									}

									return {
										...input,
										metadata: {
											variables: newVariables,
											data: {
												...input.metadata.data,
												...missingData?.reduce(
													(init, v) => ({
														...init,
														[v.size]: "",
													}),
													{},
												),
											},
										},
									};
								});

							return {
								...init,
								[key]: newValue,
							};
						}, {});

					const removedOperators = new Set(
						Array.from(removedItems.values())
							.filter((item) => item.type === "operator")
							.map((item) => item.name),
					);

					const removedMemories = new Set(
						Array.from(removedItems.values())
							.filter((item) => item.type === "memoryRef")
							.map((item) => item.name),
					);

					if (removedOperators.size > 0) {
						toast.info("Operators Removed for this version", {
							description: [...removedOperators].join(", "),
						});
					}
					if (removedMemories.size > 0) {
						toast.info("Memories Removed for this version", {
							description: [...removedMemories].join(", "),
						});
					}
				}

				if (ioErrors.length) {
					toast.info(<Toast title="Missing IO Errors:" errors={ioErrors} />);
				}

				Object.keys(action.payload.memory).forEach((name) => {
					if (state.memories[name] === undefined) {
						state.memories[name] = [];
					}
				});
			})
			.addCase(saveProjectOneDrive.fulfilled, (state, action) => {
				if (state.oneDrive) {
					if (action.payload?.lastModifiedDateTime) {
						state.oneDrive.lastUpdate = action.payload.lastModifiedDateTime;
					}

					if (action.payload?.id) {
						state.oneDrive.itemId = action.payload.id;
					}

					if (action.payload?.parentReference) {
						state.oneDrive.driveId = action.payload.parentReference.driveId;
					}
				}
			});
	},
});

const buildPath = (
	item: ConnectionIoMemoryRef,
	connections: Connection[],
	memoryRefs: MemoryRef[],
): any => {
	const source = memoryRefs.find(
		(memo) => memo.id == item.memoryRefId,
	)?.sourceMemoryId;
	const paths: ConnectionIo[] = [{ ...item, memoryRefId: source! }];
	const pathsWithInvisibleVars: ConnectionIo[] = [
		{ ...item, memoryRefId: source! },
	];

	let idx = 0;

	for (const i of connections) {
		const last = paths.at(-1);
		let lastFromId =
			last?.type == "operator" ? last.operatorId : last?.memoryRefId;
		let toId = null;
		const { to, from } = i;

		if (to.type == "operator") {
			toId = to.operatorId;
		} else {
			toId = memoryRefs.find(
				(memo) => memo.id == to.memoryRefId,
			)?.sourceMemoryId;
		}

		if (lastFromId == toId) {
			let fromId = null;

			if (from.type == "memoryRef") {
				fromId = memoryRefs.find(
					(memo) => memo.id == from.memoryRefId,
				)?.sourceMemoryId;
			}

			paths.push({
				...from,
				...(fromId != null && {
					memoryRefId: fromId,
				}),
			});

			if (last?.type == "operator" && from.type == "operator") {
				pathsWithInvisibleVars.push({
					type: "memoryRef",
					ioType: "input",
					memoryRefId: `variable-${idx}`,
				});
				idx += 1;
			}

			pathsWithInvisibleVars.push({
				...from,
				...(fromId != null && {
					memoryRefId: fromId,
				}),
			});
		}
	}

	return pathsWithInvisibleVars;
};

export const calculateRam = (): AppThunk => (dispatch, getState) => {
	const logics = selectLogics(getState());
	const logicsMemories = logics.memory;
	const logicsOperator = logics.operator;
	const sheets = getState().editor.sheets;
	const memories = getState().editor.memories;
	const arrayGroups: Array<Array<Memory | Operator>> = [];
	let indexToRemoveDuplicatedGroup: number | null = null;
	let devices: Record<string, number> = {};
	let reusableVars: Record<string, number> = {};
	let multiOuputsOperators: Record<string, Record<number, number>> = {};
	let multiInputsOperators: Record<string, Record<number, number>> = {};

	sheets.forEach((sheet) => {
		const lastBlocks = sheet.connections.filter((connection) => {
			const { from, to } = connection;

			if (to.type == "operator") {
				if (multiInputsOperators[to.operatorId] == undefined) {
					multiInputsOperators[to.operatorId] = {};
				}

				if (multiInputsOperators[to.operatorId][to.ioGroup] == undefined) {
					multiInputsOperators[to.operatorId] = {
						...multiInputsOperators[to.operatorId],
						[to.ioGroup]: 0,
					};
				}

				multiInputsOperators[to.operatorId][to.ioGroup] += 1;
			}

			if (from.type == "operator") {
				if (multiOuputsOperators[from.operatorId] == undefined) {
					multiOuputsOperators[from.operatorId] = {};
				}

				if (multiOuputsOperators[from.operatorId][from.ioGroup] == undefined) {
					multiOuputsOperators[from.operatorId] = {
						...multiOuputsOperators[from.operatorId],
						[from.ioGroup]: 0,
					};
				}

				multiOuputsOperators[from.operatorId][from.ioGroup] += 1;
			}

			return (
				to.type == "memoryRef" &&
				!sheet.connections.find((conn) => {
					const { from } = conn;

					return from.type == "memoryRef" && from.memoryRefId == to.memoryRefId;
				})
			);
		});

		lastBlocks.forEach((block) => {
			const groupWithReusableVar: Array<Memory | Operator> = [];

			const path: Array<ConnectionIo> = buildPath(
				block.to as ConnectionIoMemoryRef,
				sheet.connections,
				sheet.memoryRefs,
			);

			if (!path) return;

			const blocks: Array<ConnectionIo> = path;

			blocks.forEach((block) => {
				if (block.type == "operator") {
					const operator = sheet.operators.find(
						(op) => op.id == block.operatorId,
					);
					if (operator) groupWithReusableVar.push(operator);
				} else if (block.memoryRefId?.includes("variable")) {
					const name = block.memoryRefId.split("-");
					groupWithReusableVar.push({
						id: block.memoryRefId,
						label: "VARIABLE",
						name: `inviVariable-${name[1]}`,
						source: { type: "local", deviceId: undefined },
						broadcast: false,
						metadata: { variables: {}, data: {} },
					});
				} else {
					const memoryRef = sheet.memoryRefs.find(
						(memo) => memo.sourceMemoryId == block.memoryRefId,
					);
					if (memoryRef) {
						const memo = memories[memoryRef.name].find(
							(m) => m.id == block.memoryRefId,
						);
						if (memo) groupWithReusableVar.push(memo);
					}
				}
			});

			arrayGroups.push(groupWithReusableVar);
		});
	});

	arrayGroups.sort((a, b) => {
		const aLength = a.length;
		const bLength = b.length;

		return a[aLength - 1]?.id?.localeCompare(b[bLength - 1]?.id, undefined);
	});

	const groupsFromDifferentSheets = arrayGroups.map((group, index) => {
		const foundSameGroup = arrayGroups.find((grp, idx) => {
			const isSameGroup = grp.find(
				(g, idx) => idx > 0 && g?.id == group[0]?.id,
			);
			if (isSameGroup) indexToRemoveDuplicatedGroup = idx;
			return isSameGroup;
		});

		if (foundSameGroup) return [...foundSameGroup, ...group];

		if (indexToRemoveDuplicatedGroup == index) return null;

		return group;
	});

	const finalGroupsToSum = [groupsFromDifferentSheets[0]];

	groupsFromDifferentSheets.forEach((group, idx) => {
		if (idx == 0 || !group) return;
		const lastPositionFinalGroup = finalGroupsToSum.length - 1;
		const lastBlockPositionFinalGroup =
			finalGroupsToSum[lastPositionFinalGroup]!.length - 1;
		const lastBlockPositiionCurrentGroup = group.length - 1;

		if (
			finalGroupsToSum[lastPositionFinalGroup]![lastBlockPositionFinalGroup]
				?.id == group[lastBlockPositiionCurrentGroup]?.id
		) {
			if (
				(finalGroupsToSum[lastPositionFinalGroup]![0] as Memory)?.source
					?.deviceId != (group[0] as Memory)?.source?.deviceId
			) {
				const tempGroup = group[0];
				const tempFinalGroup = finalGroupsToSum[lastPositionFinalGroup]![0];

				finalGroupsToSum[lastPositionFinalGroup]?.unshift(tempGroup);
				group.unshift(tempFinalGroup);
			}
		}

		finalGroupsToSum.push(group);
	});

	const finalGroupsWithVariables = finalGroupsToSum.map((group) => {
		const uniqGroups = uniqBy(group, "id");

		const deviceId: string = (uniqGroups[0] as Memory)?.source?.deviceId ?? "";
		const variables = uniqGroups.filter((u) => u.name.startsWith("invi"));

		if (!deviceId) return;

		if (reusableVars[deviceId] == undefined) {
			reusableVars[deviceId] = 0;
		} else {
			reusableVars[deviceId] =
				reusableVars[deviceId] > variables.length
					? reusableVars[deviceId]
					: variables.length;
		}
		return uniqGroups;
	});

	finalGroupsWithVariables.forEach((group) => {
		if (!group || !group.length) return;

		const deviceId: string = (group[0] as Memory).source.deviceId ?? "";

		group.forEach((block) => {
			if (!block.name.startsWith("invi")) {
				let size = 0;

				if ((block as Operator)?.inputs) {
					const logic = logicsOperator[block.name];

					const outputs = logic.outputs;
					const inputs = logic.inputs;

					const sizeOutputs = outputs.reduce((sum, output, ioGroup) => {
						const operatorOutput = (block as Operator).outputs.find(
							(o) => o.name == output.label,
						);

						if (!operatorOutput!.enabled) return sum;

						switch (output.connection.mode) {
							case "multiple":
								const count = multiOuputsOperators[block.id][ioGroup];
								const sizeMultiple = count * output.size;

								return sum + sizeMultiple;
							case "spread":
								const counterSpread = output.connection.counter;
								if (
									typeof block.metadata.variables[counterSpread].content
										.value == "number"
								) {
									const value =
										block.metadata.variables[counterSpread].content.value == 0
											? 1
											: block.metadata.variables[counterSpread].content.value;
									const sizeSpread = value * output.size;
									return sum + sizeSpread;
								}

								return sum;
							case "single":
								return sum;
							default:
								return sum;
						}
					}, logic.size);

					size = inputs.reduce((sum, input, ioGroup) => {
						const operatorInput = (block as Operator).inputs.find(
							(i) => i.name == input.label,
						);

						if (!operatorInput!.enabled) return sum;

						switch (input.connection.mode) {
							case "multiple":
								const count = multiInputsOperators[block.id][ioGroup];
								const sizeMultiple = count * input.size;

								return sum + sizeMultiple;
							case "spread":
								const counterSpread = input.connection.counter;
								if (
									typeof block.metadata.variables[counterSpread].content
										.value == "number"
								) {
									const value =
										block.metadata.variables[counterSpread].content.value == 0
											? 1
											: block.metadata.variables[counterSpread].content.value;
									const sizeSpread = value * input.size;
									return sum + sizeSpread;
								}

								return sum;

							case "single":
								return sum;
							default:
								return sum;
						}
					}, sizeOutputs);
				} else {
					const sizeVariables = Object.values(block.metadata.data).reduce(
						(sum, variable) => {
							if (isArray(variable)) {
								return sum + variable.reduce((acc, v) => acc + v.length, 0);
							}
							return sum + variable.length;
						},
						0,
					);

					size = logicsMemories[block.name].size + sizeVariables;
				}

				if (devices[deviceId] == undefined) {
					devices[deviceId] = 0;
				}

				devices[deviceId] += size;
			}
		});
	});

	const variableSize = logicsMemories["VARIABLE"].size;

	Object.entries(reusableVars).forEach(([deviceId, variables]) => {
		devices[deviceId] += variables * variableSize;
	});

	dispatch(updateRam(devices));
};

export const createOperators =
	(operators: Operator[]): AppThunk =>
	(dispatch, getState) => {
		let operatorsRefs = {} as Record<string, string>;

		const newOperators = operators.map((operator) => {
			const newId = generateUniqueId("operator");
			const oldId = operator.id;

			operatorsRefs[oldId] = newId;

			return {
				...operator,
				id: newId,
				sheetId: getState().editor.sheets[getState().editor.currentTabIndex].id,
			};
		});

		localStorage.setItem(
			"configurator-old:operators",
			JSON.stringify(operatorsRefs),
		);

		dispatch(editorSlice.actions.insertOperator({ operator: newOperators }));
	};

export const createMemoryRefs =
	(memoryRefs: MemoryRef[]): AppThunk =>
	(dispatch, getState) => {
		let memoriesRefs = {} as Record<string, string>;
		const newMemoryRefs = memoryRefs.map((memory) => {
			const newId = generateUniqueId("memoryRef");
			const oldId = memory.id;

			memoriesRefs[oldId] = newId;

			return {
				...memory,
				id: newId,
				sheetId: getState().editor.sheets[getState().editor.currentTabIndex].id,
			};
		});

		localStorage.setItem(
			"configurator-old:memories",
			JSON.stringify(memoriesRefs),
		);

		dispatch(editorSlice.actions.insertMemoryRef({ memoryRef: newMemoryRefs }));
	};

export const createConnections =
	(sheetIndex: number, connections: Connection[]): AppThunk =>
	(dispatch, getState) => {
		const newMemories = getState().editor.sheets[sheetIndex].memoryRefs;
		const newOperators = getState().editor.sheets[sheetIndex].operators;
		const oldMemories = JSON.parse(
			localStorage.getItem("configurator-old:memories") || "",
		);
		const oldOperators = JSON.parse(
			localStorage.getItem("configurator-old:operators") || "",
		);

		const newConnections = connections.map((connection) => {
			let from = null;
			let to = null;

			const { from: origin, to: destination } = connection;

			if (origin.type === "memoryRef") {
				from = newMemories.find(
					(memory) => memory.id === oldMemories[origin.memoryRefId],
				) as MemoryRef;
			} else {
				from = newOperators.find(
					(operator) => operator.id === oldOperators[origin.operatorId],
				) as Operator;
			}

			if (destination.type === "memoryRef") {
				to = newMemories.find(
					(memory) => memory.id === oldMemories[destination.memoryRefId],
				) as MemoryRef;
			} else {
				to = newOperators.find(
					(operator) => operator.id === oldOperators[destination.operatorId],
				) as Operator;
			}

			return {
				id: generateUniqueId("connection"),
				from: {
					...origin,
					[connection.from.type === "memoryRef" ? "memoryRefId" : "operatorId"]:
						from.id,
				},
				to: {
					...destination,
					[connection.to.type === "memoryRef" ? "memoryRefId" : "operatorId"]:
						to.id,
				},
				error: false,
			};
		});

		dispatch(editorSlice.actions.createConnection(newConnections));
	};

export const createOperatorPreview =
	(
		operatorName: string,
		sheetIndex: number,
		position: Point2D,
		trigger: BlockPreviewTrigger,
	): AppThunk =>
	(dispatch, getState) => {
		const logics = selectLogics(getState());
		const logicsOperator = logics.operator[operatorName];

		console.log({ config: logicsOperator });

		const operator: Operator = {
			id: generateUniqueId("operator"),
			label: operatorName,
			name: operatorName,
			inputs: logicsOperator.inputs.map((input) => ({
				name: input.label,
				enabled: input.enabledByDefault,
			})),
			outputs: logicsOperator.outputs.map((output) => ({
				name: output.label,
				enabled: output.enabledByDefault,
			})),
			metadata: {
				variables: Object.fromEntries(
					logicsOperator.variables.map((variable) => [
						variable.name,
						variableFromLogics(variable),
					]),
				),
				data: logicsOperator.data
					? Object.fromEntries(
							logicsOperator.data.map((data) => [data.size, ""]),
						)
					: {},
			},
			sheetId: getState().editor.sheets[sheetIndex].id,
			boundingBox: {
				left: position.x,
				top: position.y,
				width: 0,
				height: 0,
			},
			error: false,
		};

		const preview: BlockPreview = {
			type: "operator",
			block: operator,
		};

		dispatch(editorSlice.actions.setBlockPreview({ preview, trigger }));

		console.log({ preview });
	};

export const createMemoryRefPreview =
	(
		memoryName: string,
		sheetIndex: number,
		position: Point2D,
		trigger: BlockPreviewTrigger,
	): AppThunk =>
	(dispatch, getState) => {
		const memoryRef: MemoryRef = {
			id: generateUniqueId("memoryRef"),
			name: memoryName,
			sheetId: getState().editor.sheets[sheetIndex].id,
			boundingBox: {
				left: position.x,
				top: position.y,
				width: 0,
				height: 0,
			},
			sourceMemoryId: null,
			error: false,
		};

		const preview: BlockPreview = {
			type: "memoryRef",
			block: memoryRef,
		};

		dispatch(editorSlice.actions.setBlockPreview({ preview, trigger }));

		console.log({ preview });
	};

export const insertPreview = (): AppThunk => (dispatch, getState) => {
	const blockPreview = getState().editor.blockPreview;

	if (!blockPreview) {
		return;
	}

	switch (blockPreview.type) {
		case "operator":
			dispatch(
				editorSlice.actions.insertOperator({
					operator: blockPreview.block,
				}),
			);
			break;
		case "memoryRef":
			dispatch(
				editorSlice.actions.insertMemoryRef({
					memoryRef: blockPreview.block,
				}),
			);
			break;
	}
	dispatch(
		editorSlice.actions.setBlockPreview({ preview: null, trigger: null }),
	);
};

export const cancelPreview = (): AppThunk => (dispatch, getState) => {
	dispatch(
		editorSlice.actions.setBlockPreview({ preview: null, trigger: null }),
	);
};

const deleteSelectionLeaf =
	(selection: Selection): AppThunk =>
	(dispatch, getState) => {
		if (selection === null) {
			return;
		}

		switch (selection.type) {
			case "operator":
				dispatch(
					editorSlice.actions.deleteOperator({
						sheetIndex: getState().editor.currentTabIndex,
						operatorId: selection.operatorId,
					}),
				);
				break;
			case "memoryRef":
				dispatch(
					editorSlice.actions.deleteMemoryRef({
						sheetIndex: getState().editor.currentTabIndex,
						memoryRefId: selection.memoryRefId,
					}),
				);
				break;
			case "connection":
				dispatch(
					editorSlice.actions.deleteConnection({
						id: selection.connectionId,
					}),
				);
				break;
		}
	};

export const deleteSelection = (): AppThunk => (dispatch, getState) => {
	const selection =
		getState().editor.sheets[getState().editor.currentTabIndex].selection;

	if (selection === null) {
		return;
	}

	dispatch(editorSlice.actions.addUndo());

	if (selection.type === "multiple") {
		selection.selection.forEach((selection) =>
			dispatch(deleteSelectionLeaf(selection)),
		);
		dispatch(editorSlice.actions.setSelection(null));
		return;
	}

	dispatch(deleteSelectionLeaf(selection));
	dispatch(editorSlice.actions.setSelection(null));
};

export const toggleFromSelection =
	(selection: Selection): AppThunk =>
	(dispatch, getState) => {
		const currentTabIndex = getState().editor.currentTabIndex;
		const currentSelection =
			getState().editor.sheets[currentTabIndex].selection;

		if (currentSelection === null) {
			dispatch(editorSlice.actions.setSelection(selection));
			return;
		}

		if (currentSelection.type === "multiple") {
			if (isInSelection(currentSelection, selection)) {
				dispatch(editorSlice.actions.removeFromSelection(selection));
			} else {
				dispatch(editorSlice.actions.addToSelection(selection));
			}
			return;
		}

		if (isSameSelection(currentSelection, selection)) {
			dispatch(editorSlice.actions.setSelection(null));
		} else {
			dispatch(editorSlice.actions.addToSelection(selection));
		}
	};

export const selectIoOrCreateConnection =
	(io: SelectionIo): AppThunk =>
	(dispatch, getState) => {
		const currentTabIndex = getState().editor.currentTabIndex;
		const currentSelection =
			getState().editor.sheets[currentTabIndex].selection;

		if (currentSelection === null) {
			dispatch(editorSlice.actions.setSelection(io));
			return;
		}

		if (isSameSelection(currentSelection, io)) {
			dispatch(editorSlice.actions.setSelection(null));
		} else if (currentSelection.type === "io") {
			if (
				currentSelection.io.type === "operator" ||
				io.io.type === "operator"
			) {
				const operatorToCheckConnection =
					currentSelection.io.type === "operator" ? currentSelection : io;

				const connections: Connection[] =
					getState().editor.sheets[currentTabIndex].connections;
				const logics = selectLogics(getState());

				const key =
					operatorToCheckConnection.io.ioType === "output"
						? "outputs"
						: "inputs";

				const ioGroup = (operatorToCheckConnection.io as ConnectionIoOperator)
					.ioGroup;

				const logicOperator =
					logics.operator[operatorToCheckConnection.name][key][ioGroup];

				let memoryRefId = null;

				if (io.io.type === "memoryRef") memoryRefId = io.io.memoryRefId;
				if (currentSelection.io.type === "memoryRef")
					memoryRefId = currentSelection.io.memoryRefId;

				if (memoryRefId) {
					const memoryRef = getState().editor.sheets[
						currentTabIndex
					].memoryRefs.find((ref) => ref.id === memoryRefId);

					if (
						memoryRef &&
						logicOperator.allowedMemories &&
						logicOperator.allowedMemories.length > 0 &&
						!logicOperator.allowedMemories.includes(`MEMORY_${memoryRef.name}`)
					) {
						toast.error(
							t("cannotConnect", {
								operator: operatorToCheckConnection.name,
								memory: memoryRef.name,
							}),
						);
						dispatch(editorSlice.actions.setSelection(null));
						return;
					}
				}

				if (logicOperator.connection.mode === "single") {
					if (operatorToCheckConnection.io.ioType === "input") {
						const found = connections.find((connection) => {
							return (
								(connection.to as ConnectionIoOperator).operatorId ===
									(operatorToCheckConnection.io as ConnectionIoOperator)
										.operatorId &&
								(connection.to as ConnectionIoOperator).ioGroup === ioGroup
							);
						});

						if (found) {
							toast.error(
								t("singleConnectionError", {
									operator: operatorToCheckConnection.name,
								}),
							);
							dispatch(editorSlice.actions.setSelection(null));
							return;
						}
					} else {
						const found = connections.find((connection) => {
							return (
								(connection.from as ConnectionIoOperator).operatorId ===
									(operatorToCheckConnection.io as ConnectionIoOperator)
										.operatorId &&
								(connection.from as ConnectionIoOperator).ioGroup === ioGroup
							);
						});

						if (found) {
							toast.error(
								t("singleConnectionError", {
									operator: operatorToCheckConnection.name,
								}),
							);
							dispatch(editorSlice.actions.setSelection(null));
							return;
						}
					}
				}
			}

			dispatch(
				editorSlice.actions.createConnection({
					id: generateUniqueId("connection"),
					origin: currentSelection.io,
					destination: io.io,
				}),
			);
			dispatch(editorSlice.actions.setSelection(null));
		} else {
			dispatch(editorSlice.actions.setSelection(io));
		}
	};

export const {
	resetEditor,
	loadEditorState,
	setSideBarSelection,
	selectTab,
	moveTab,
	createSheet,
	deleteSheet,
	moveSheetByOffset,
	moveSheetTo,
	setSheetGridSize,
	updateSheetTitle,
	updateSheetScale,
	updateProjectName,
	setBlockPreview,
	updateBlockPreviewBoundingBox,
	updateBlockBoundingBox,
	updateMemoryRefBoundingBoxByIndex,
	updateOperatorBoundingBoxByIndex,
	updateBlockVariableContent,
	updateBlockDataValue,
	updateMemoryRefSource,
	insertMemory,
	updateMemory,
	createDeviceSlot,
	updateDeviceSlot,
	setSelection,
	addToSelection,
	selectMultiple,
	removeFromSelection,
	deleteDeviceSlot,
	clearDeviceSlot,
	deleteOperator,
	deleteMemoryRef,
	deleteMemory,
	deleteMemories,
	insertOperator,
	insertMemoryRef,
	updateMemoryRef,
	updateOperator,
	updateConnection,
	createConnection,
	deleteConnection,
	updateOperatorEnabledIos,
	addUndo,
	undo,
	redo,
	updateRam,
} = editorSlice.actions;

export const selectProjectName = (state: RootState) => state.editor.projectName;

export const selectSheets = (state: RootState) => state.editor.sheets;
export const selectSheet = (index: number): GenericSelector<Sheet> =>
	createSelector(selectSheets, (sheets) => sheets[index]);

export const selectEditor = (state: RootState) => state.editor;
export const selectMemories = (state: RootState) => state.editor.memories;
export const selectDevices = (state: RootState) => state.editor.devices;
export const selectCurrentTabIndex = (state: RootState) =>
	state.editor.currentTabIndex;

export default editorSlice.reducer;
